import * as React from 'react';
import { useUser } from '@modules/user';
import type { Comment, Entity } from '@modules/build-hub-comment/types';
import { Avatar, AvatarFallback, AvatarImage } from '@rocco/components/avatar';
import {
    decodeMessage,
    toEntities,
} from '@modules/build-hub-comment/utils/comment-parser';
import { getInitials } from '@modules/build-hub/utils/get-initials';
import { useBuildHubData } from '@modules/build-hub/hooks/use-build-hub-data';
import { timeToNow } from '@modules/date-time/util/utc-date-to-local-timezone';

export function CommentViewer({ comments }: { comments: Comment[] }) {
    const user = useUser();
    const { buildHub } = useBuildHubData();

    if (!comments?.length) {
        return <p className="text-sm text-gray-500">No comments yet.</p>;
    }

    return (
        <ul className="space-y-3 pb-4">
            {comments.map((comment) => {
                const { id, message, createdAt, author, mentions, files } =
                    comment;

                const isMine = author.id === user.ID;
                const messageEntities = toEntities(
                    mentions ?? [],
                    files ?? [],
                    buildHub.owner
                );
                return (
                    <li key={id} className={`flex items-start gap-3 py-2`}>
                        <Avatar size="square40" borderWidth="none">
                            <AvatarImage
                                src={author.avatar ?? undefined}
                                alt={author.name}
                                fit="cover"
                            />
                            <AvatarFallback size="md">
                                {author.name ? getInitials(author.name) : '?'}
                            </AvatarFallback>
                        </Avatar>

                        <div
                            className={`relative w-full text-sm text-gray-900`}
                        >
                            <div className="flex flex-row items-center gap-2">
                                <div className="text-sm font-medium">
                                    {author.name}
                                </div>
                                {createdAt && (
                                    <div className={`text-xs text-gray-400`}>
                                        {timeToNow(createdAt)}
                                    </div>
                                )}
                            </div>
                            <div className="whitespace-pre-wrap text-sm">
                                {renderMessageWithMentions(
                                    message,
                                    messageEntities,
                                    isMine
                                )}
                            </div>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

/**
 * Render a message with mentions styled in lighter blue.
 * Uses decodeMessage + entities so multi-word mentions render correctly.
 */
function renderMessageWithMentions(
    rawMessage: string,
    entities: Entity[],
    isMine: boolean
) {
    const decoded = decodeMessage(rawMessage, entities);
    const sortedEntities = [...entities].sort(
        (a, b) => b.label.length - a.label.length
    );

    let parts: (string | JSX.Element)[] = [decoded];

    for (const ent of sortedEntities) {
        const next: (string | JSX.Element)[] = [];
        const needle = `@${ent.label}`;

        parts.forEach((p, i) => {
            if (typeof p === 'string') {
                const split = p.split(needle);
                split.forEach((chunk, j) => {
                    if (chunk) next.push(chunk);
                    if (j < split.length - 1) {
                        next.push(
                            <span
                                key={`${ent.type}:${ent.id}:${i}:${j}`}
                                style={{
                                    color: isMine ? '#60a5fa' : '#2563eb', // lighter blue if mine
                                    fontWeight: 500,
                                }}
                            >
                                {needle}
                            </span>
                        );
                    }
                });
            } else {
                next.push(p);
            }
        });

        parts = next;
    }

    return <>{parts}</>;
}
